import { useState, type ReactNode } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useIsFocused } from "expo-router/react-navigation";

import { ThemedText } from '@/components/ThemedText';
import { useScale } from '@/hooks/useScale';

type Props = {
  title: string;
  children: ReactNode;
};

export default function CollapsibleDebugSection({ title, children }: Props) {
  const [shown, setShown] = useState(false);
  const isFocused = useIsFocused();
  const active = shown && isFocused;
  const styles = useStyles();

  return (
    <View style={styles.section}>
      <View style={styles.header}>
        <ThemedText style={styles.title}>{title}</ThemedText>
        <Pressable
          onPress={() => setShown((s) => !s)}
          style={({ focused }) => [
            styles.button,
            focused && styles.buttonFocused,
          ]}
        >
          <ThemedText style={styles.buttonText}>
            {shown ? 'Hide' : 'Show'}
          </ThemedText>
        </Pressable>
      </View>
      {active && <View style={styles.body}>{children}</View>}
    </View>
  );
}

function useStyles() {
  const scale = useScale();
  return StyleSheet.create({
    section: {
      backgroundColor: 'rgba(255,255,255,0.1)',
      borderRadius: 12 * scale,
      padding: 20 * scale,
      gap: 10 * scale,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 12 * scale,
    },
    title: {
      color: '#fff',
      fontSize: 18 * scale,
      fontWeight: '700',
      flex: 1,
    },
    body: {
      gap: 10 * scale,
    },
    button: {
      paddingVertical: 8 * scale,
      paddingHorizontal: 16 * scale,
      borderRadius: 8 * scale,
      borderWidth: 2,
      borderColor: 'transparent',
      backgroundColor: 'rgba(255,255,255,0.15)',
    },
    buttonFocused: {
      backgroundColor: 'rgba(255,255,255,0.3)',
      borderColor: '#fff',
    },
    buttonText: {
      color: '#fff',
      fontSize: 13 * scale,
      fontWeight: '600',
    },
  });
}
